// Position Tracker toolbar presentation — extracted from index.tsx (#340).
//
// The sort segment, the card-order lock, the filter chip and its two-row
// selector, and the P&L refresh ring. Every control here takes its state and
// its callbacks as props: nothing reads a store, subscribes to the feed, or
// mutates an order.
//
// Two contracts in here are easy to break by eye and are pinned by
// ./trackerToolbar.test.tsx:
//
//   - the h-[22px] shared by the sort segment and the lock toggle. Both mount
//     CONDITIONALLY (card mode, rows present) beside the always-present
//     ViewModeToggle, so a taller control grows the toolbar row and reflows
//     the page under the trader's cursor (Brendan, Jul 9).
//   - the chip's tri-state colour grammar: blue include, red strikethrough
//     exclude, neutral off — with tone="deep" darkening ONLY the include state
//     so the two chip rows read as different selectors.
//     Dark is the default theme; the `light:` classes are light-theme-only
//     contrast overrides (#980) and leave the dark rendering untouched.
import { useEffect, useState } from 'react';
import { ArrowDownWideNarrow, ArrowUpWideNarrow, Lock, LockOpen, X } from 'lucide-react';
import type { SortDirection } from '../../components/atoms/SortableTh';
import type { SportFilter } from '../../lib/kalshiTicker/sportFilter';
import { SPORT_TABS, type Sport } from '../ValueDashboard/types';
import {
  TRACKER_FAMILY_CHIPS,
  type TrackerFamilyFilter,
  type TrackerMarketFamily,
} from './trackerMarketFamily';

export type ActiveSortDir = Exclude<SortDirection, null>;

export function SortSegmentButton({
  active,
  dir,
  label,
  title,
  onClick,
}: {
  active: boolean;
  dir: ActiveSortDir;
  label: string;
  title: string;
  onClick: () => void;
}) {
  const Icon = dir === 'asc' ? ArrowUpWideNarrow : ArrowDownWideNarrow;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      /* h-[22px] matches ViewModeToggle's button height (py-1 + 14px icon)
         so this group's bordered box lands at the same 24px as the always-
         present view toggle — mounting it (card mode only) no longer grows
         the toolbar row and reflows the page (Brendan, Jul 9). */
      className={`inline-flex h-[22px] items-center gap-1.5 px-2 text-[11px] font-medium transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-300'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <span>{label}</span>
      {active && <Icon size={12} />}
    </button>
  );
}

/** Card-order Lock toggle (issue #97). Locked holds the captured card order
 *  while contents keep updating; unlocked resumes live sorting. Height matches
 *  SortSegmentButton / ViewModeToggle (h-[22px]) so mounting it never reflows
 *  the toolbar row. */
export function LockToggleButton({ locked, onClick }: { locked: boolean; onClick: () => void }) {
  const Icon = locked ? Lock : LockOpen;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={locked}
      title={
        locked
          ? 'Card order locked — cards hold their place while price, queue, size and status keep updating. Click to unlock and resume live sorting.'
          : 'Lock the current card order so cards stop jumping on live resorts while their contents keep updating. Choosing a sort unlocks.'
      }
      className={`inline-flex h-[22px] items-center gap-1 rounded-md border px-2 text-[11px] font-medium transition-colors ${
        locked
          ? 'border-blue-500/50 bg-blue-500/20 text-blue-300'
          : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <Icon size={12} />
      <span>{locked ? 'Locked' : 'Lock'}</span>
    </button>
  );
}

export function SportFilterChip({
  mode,
  label,
  onClick,
  cycleHint,
  tone = 'default',
}: {
  mode: 'off' | 'include' | 'exclude';
  label: string;
  onClick: () => void;
  cycleHint?: boolean;
  /** 'deep' = the second-row market-family selector: a darker blue include
   *  highlight so the two chip rows read as different selectors at a glance.
   *  Deliberately still blue (green/red are reserved for betting) and the
   *  exclude state stays the shared red-strikethrough convention. */
  tone?: 'default' | 'deep';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={mode !== 'off'}
      title={
        cycleHint
          ? `Click: only ${label} → exclude ${label} → back to all`
          : undefined
      }
      className={`inline-flex h-7 items-center rounded-md border px-2.5 text-[11px] font-medium transition-colors ${
        mode === 'include'
          ? tone === 'deep'
            ? 'border-blue-700/70 bg-blue-900/50 text-blue-400 light:border-blue-700 light:bg-blue-700 light:text-white'
            : 'border-blue-500/50 bg-blue-500/20 text-blue-300 light:border-blue-400 light:bg-blue-100 light:text-blue-800'
          : mode === 'exclude'
            ? 'border-red-500/50 bg-red-500/20 text-red-400 line-through light:text-red-700'
            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {/* Exclude is conveyed by the red strikethrough alone (matches the org
          page). No X glyph — it widened the chip on the exclude toggle. */}
      {label}
    </button>
  );
}

/** The sport chips and the market-family line below them (Prof, Jul 7) — each
 *  scrolls on overflow, never wraps. The two lines AND together (Soccer +
 *  Total = soccer totals only).
 *
 *  Open Positions and Open Orders each mount their own copy over their own
 *  filter state (Dan, Jul 7: a trader focuses one sport's positions while
 *  still watching every resting order), which is why `scope` exists — it names
 *  the list in the group labels a screen reader announces, and nothing else.
 *
 *  #980: rows whose market family hasn't loaded yet pass every family chip;
 *  `unclassifiedCount` says how many of the listed rows are there only for
 *  that reason. The one-time migration notice explains why a saved Moneyline
 *  chip now shows as Moneyline + Props + Other. */
export function SportMarketFilterRows({
  scope,
  className,
  sportFilter,
  kindFilter,
  onAllSports,
  onSportToggle,
  onAllKinds,
  onKindToggle,
  unclassifiedCount = 0,
  migrationNotice = false,
  onDismissMigrationNotice,
}: {
  scope: 'positions' | 'open orders';
  /** Extra layout classes for the row stack — the positions copy sits in the
   *  toolbar's flexible middle column and takes `flex-1`; the orders copy is
   *  a full-width block in its section header and takes nothing. */
  className?: string;
  sportFilter: SportFilter;
  kindFilter: TrackerFamilyFilter;
  onAllSports: () => void;
  onSportToggle: (sport: Sport) => void;
  onAllKinds: () => void;
  onKindToggle: (kind: TrackerMarketFamily) => void;
  /** Listed rows whose family is still loading. Shown only while a family
   *  chip is active — under All markets nothing is being filtered. */
  unclassifiedCount?: number;
  migrationNotice?: boolean;
  onDismissMigrationNotice?: () => void;
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-1${className ? ` ${className}` : ''}`}>
      <div
        className="min-w-0 overflow-x-auto"
        role="group"
        aria-label={`Filter ${scope} by sport. Click a sport to include it, again to exclude it (red), a third time to clear.`}
      >
        <div className="flex w-max items-center gap-1">
          <SportFilterChip
            mode={sportFilter.size === 0 ? 'include' : 'off'}
            label="All sports"
            onClick={onAllSports}
          />
          {SPORT_TABS.map((sport) => (
            <SportFilterChip
              key={sport.id}
              mode={sportFilter.get(sport.id) ?? 'off'}
              label={sport.label}
              onClick={() => onSportToggle(sport.id)}
              cycleHint
            />
          ))}
        </div>
      </div>
      <div
        className="min-w-0 overflow-x-auto"
        role="group"
        aria-label={`Filter ${scope} by market type (moneyline, spread, total, props, other). Combines with the sport filter.`}
      >
        <div className="flex w-max items-center gap-1">
          <SportFilterChip
            mode={kindFilter.size === 0 ? 'include' : 'off'}
            label="All markets"
            onClick={onAllKinds}
            tone="deep"
          />
          {TRACKER_FAMILY_CHIPS.map((k) => (
            <SportFilterChip
              key={k.id}
              mode={kindFilter.get(k.id) ?? 'off'}
              label={k.label}
              onClick={() => onKindToggle(k.id)}
              cycleHint
              tone="deep"
            />
          ))}
          {kindFilter.size > 0 && unclassifiedCount > 0 && (
            <span
              className="ml-1 whitespace-nowrap text-[11px] text-muted-foreground"
              title="Market type not known yet. These rows show under every market filter until it is."
            >
              {unclassifiedCount} unclassified
            </span>
          )}
        </div>
      </div>
      {migrationNotice && (
        <div
          role="status"
          className="flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground"
        >
          <span className="truncate">
            Moneyline now splits into Moneyline, Props and Other. Your saved filter kept all three.
          </span>
          <button
            type="button"
            onClick={onAllKinds}
            className="shrink-0 whitespace-nowrap rounded px-1 text-blue-400 hover:underline light:text-blue-700"
          >
            Reset market filter
          </button>
          <button
            type="button"
            onClick={onDismissMigrationNotice}
            aria-label="Dismiss market filter notice"
            className="shrink-0 rounded p-0.5 hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

export function RefreshProgress({
  anchorMs,
  intervalMs,
  refreshing,
}: {
  anchorMs: number;
  intervalMs: number;
  refreshing: boolean;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const elapsed = ((nowMs - anchorMs) % intervalMs + intervalMs) % intervalMs;
  const progress = Math.min(1, Math.max(0, elapsed / intervalMs));
  const remainingSeconds = Math.max(1, Math.ceil((intervalMs - elapsed) / 1000));
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <div
      className="text-muted-foreground inline-flex items-center gap-1 text-[10px] font-medium leading-none"
      title={`Next P&L refresh in ${remainingSeconds}s`}
      aria-label={`Next P&L refresh in ${remainingSeconds} seconds`}
    >
      <svg className="h-4 w-4 -rotate-90" viewBox="0 0 20 20" aria-hidden="true">
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.18"
          strokeWidth="2"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span className="min-w-[2ch] text-right font-mono">
        {refreshing ? '...' : `${remainingSeconds}`}
      </span>
    </div>
  );
}
